/**
 * Meta (Facebook) Pixel for ad attribution.
 *
 * The pixel ID comes from the server (`config.meta_pixel_id` on /api/init), so
 * dev and staging, which have none, never load Meta's script or send events.
 * Events fired before the session answers are held and replayed once the
 * pixel starts, or dropped if it never does.
 *
 * Purchase is also sent server-side through the Conversions API when the
 * payment is confirmed (main/services/meta_capi.py); both carry the same
 * `eventID` so Meta counts the sale once.
 */

const SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js'
const DEFERRED_KEY = 'copr:meta-pixel:deferred'
const MAX_PENDING = 50

let state = 'pending' // 'pending' | 'on' | 'off'
let pending = []

/** Event id shared with the server-side Purchase, for deduplication. */
export function purchaseEventId(paymentId) {
  return `payment-${paymentId}`
}

function load(pixelId, user) {
  /* eslint-disable */
  // Meta's base code, unminified: a queueing `fbq` stub until fbevents.js loads.
  const fbq = (window.fbq = function () {
    fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments)
  })
  if (!window._fbq) window._fbq = fbq
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []
  const script = document.createElement('script')
  script.async = true
  script.src = SCRIPT_URL
  document.head.appendChild(script)
  /* eslint-enable */

  // Advanced matching: the pixel hashes the email before it leaves the page.
  const matching = user?.email ? { em: String(user.email).trim().toLowerCase() } : undefined
  window.fbq('init', String(pixelId), matching)
}

function send({ event, params, eventID, custom }) {
  const options = eventID ? { eventID } : undefined
  window.fbq(custom ? 'trackCustom' : 'track', event, params || {}, options)
}

/**
 * Start the pixel. Call once, after the session has loaded.
 * @param {string|null|undefined} pixelId — no id turns tracking off for good.
 * @param {{ user?: { email?: string } | null }} [options]
 */
export function initMetaPixel(pixelId, { user = null } = {}) {
  if (state !== 'pending' || typeof window === 'undefined') return
  if (!pixelId) {
    state = 'off'
    pending = []
    return
  }
  load(pixelId, user)
  state = 'on'
  const queued = [...readDeferred(), ...pending]
  pending = []
  for (const item of queued) send(item)
}

/**
 * Track a standard event (PageView, ViewContent, Purchase…) or, with
 * `custom: true`, a custom one.
 */
export function track(event, params = {}, { eventID, custom = false } = {}) {
  const item = { event, params, eventID, custom }
  if (state === 'on') return send(item)
  if (state === 'pending' && pending.length < MAX_PENDING) pending.push(item)
}

/**
 * Track an event on the next page load. For call sites that leave the page
 * right away (a full-page redirect), where an event sent now would be lost
 * before Meta's script has loaded.
 */
export function trackAfterReload(event, params = {}, options = {}) {
  try {
    const items = readDeferred()
    items.push({ event, params, eventID: options.eventID, custom: !!options.custom })
    sessionStorage.setItem(DEFERRED_KEY, JSON.stringify(items.slice(-MAX_PENDING)))
  } catch { /* private mode: the event is lost, nothing else is */ }
}

function readDeferred() {
  try {
    const raw = sessionStorage.getItem(DEFERRED_KEY)
    sessionStorage.removeItem(DEFERRED_KEY)
    const items = raw ? JSON.parse(raw) : []
    return Array.isArray(items) ? items : []
  } catch {
    return []
  }
}

/** Test hook: back to the state before `initMetaPixel`. */
export function _resetMetaPixel() {
  state = 'pending'
  pending = []
}
